export type CompanyTier = 'free' | 'premium' | 'elite';

const TIER_RANK: Record<CompanyTier, number> = {
  free: 0,
  premium: 1,
  elite: 2,
};

// Routes accessible to free tier (no restriction beyond auth)
const FREE_ROUTES: string[] = [
  '/eirsaliye',
  '/settings',
];

// Routes that require elite tier (blocked for free + premium)
const ELITE_ROUTES: string[] = [
  '/known-customers',
  '/unknown-customers',
];

// Admin routes — exempt from tier checks entirely
const ADMIN_ROUTES: string[] = [
  '/admin',
];

/**
 * Returns the minimum tier required for a given route path.
 * - Free routes: accessible to all tiers
 * - Elite routes: only elite tier
 * - Admin routes: exempt (returns 'free' so TierGate passes through; admin access is handled separately)
 * - Everything else: requires premium
 */
export function getRequiredTier(path: string): CompanyTier {
  // Admin routes are exempt from tier gating
  for (const route of ADMIN_ROUTES) {
    if (path === route || path.startsWith(route + '/')) {
      return 'free';
    }
  }

  // Check free routes (exact or prefix match for parameterized routes like /eirsaliye/:id)
  for (const route of FREE_ROUTES) {
    if (path === route || path.startsWith(route + '/')) {
      return 'free';
    }
  }

  // Check elite routes
  for (const route of ELITE_ROUTES) {
    if (path === route || path.startsWith(route + '/')) {
      return 'elite';
    }
  }

  // Default: require premium (free users blocked from everything not explicitly free)
  return 'premium';
}

export function hasTierAccess(companyTier: CompanyTier | null | undefined, requiredTier: CompanyTier): boolean {
  const effective = normalizeCompanyTier(companyTier);
  return TIER_RANK[effective] >= TIER_RANK[requiredTier];
}

export function normalizeCompanyTier(raw: string | null | undefined): CompanyTier {
  if (raw === 'free' || raw === 'premium' || raw === 'elite') {
    return raw;
  }
  return 'free';
}

export function getTierDisplayName(tier: CompanyTier): string {
  const names: Record<CompanyTier, string> = {
    free: 'Free',
    premium: 'Premium',
    elite: 'Elite',
  };
  return names[tier];
}
